import React, { useReducer, useEffect, useMemo, useRef } from 'react';
import { normalizeDisplayCurrency } from '../utils/currency';
import { AppContext } from './appContextRef';
import apiService from '../services/api';
import { queryClient } from '../queryClient';
import { queryKeys } from '../api/queryKeys';
import { ownedMapsFromPurchases, fetchUserPurchases } from '../hooks/queries/useUserPurchasesQuery';
import { CoreCatalogQuerySync } from '../components/CoreCatalogQuerySync';
import { UserPurchasesQuerySync } from '../components/UserPurchasesQuerySync';
import i18n from '../i18n/config';
import { getAuthApiDisplayMessage } from '../utils/errorMessage';
import { stopAllUploadPreviewPlayback } from '../utils/uploadPreviewPlayback';

/** Map known backend auth error messages to translated strings */
function mapAuthErrorMessage(message) {
  if (!message || typeof message !== 'string') return message;
  const lower = message.toLowerCase();
  if (lower.includes('invalid') && lower.includes('credential')) return i18n.t('auth:errors.invalid_credentials');
  if (lower.includes('unable to log in') || lower.includes('provided credentials')) return i18n.t('auth:errors.invalid_credentials');
  return message;
}

/** True when request failed due to network (backend down, ECONNREFUSED, CORS, etc.) */
function isNetworkError(error) {
  if (!error) return false;
  const msg = (error.message || '').toLowerCase();
  const name = (error.name || '').toLowerCase();
  return (
    name === 'typeerror' ||
    msg.includes('failed to fetch') ||
    msg.includes('network request failed') ||
    msg.includes('networkerror') ||
    msg.includes('econnrefused') ||
    msg.includes('load failed')
  );
}

/** Only attempt refresh once per page load when no token; avoids duplicate 401 in console (e.g. React Strict Mode). */
let initRefreshAttempted = false;

/** Prevent multiple concurrent app inits (e.g. Strict Mode double-mount, or effect re-run). */
let appInitInProgress = false;

/** Ensure init from effect runs only once per page load (blocks second mount from starting another init). */
let appInitStartedFromEffect = false;

const SESSION_HINT_COOKIE = 'tw_session_hint';
const SESSION_HINT_MAX_AGE = 24 * 60 * 60;
const REMEMBER_HINT_MAX_AGE = 30 * 24 * 60 * 60;

function hasSessionHint() {
  return typeof document !== 'undefined' && document.cookie.includes(`${SESSION_HINT_COOKIE}=`);
}

function setSessionHint(rememberMe = false) {
  if (typeof document === 'undefined') return;
  const maxAge = rememberMe ? REMEMBER_HINT_MAX_AGE : SESSION_HINT_MAX_AGE;
  document.cookie = `${SESSION_HINT_COOKIE}=1; path=/; max-age=${maxAge}; SameSite=Lax`;
}

function clearSessionHint() {
  if (typeof document === 'undefined') return;
  document.cookie = `${SESSION_HINT_COOKIE}=; path=/; max-age=0`;
}

// Initial auth state: we don't read token from localStorage (token is in memory only).
// We cannot check refresh_token in JS because it's httpOnly - so we start as "not authenticated"
// and restore session by calling refresh endpoint on mount (browser sends httpOnly cookie automatically).
const getInitialAuthState = () => false;

const CART_STORAGE_KEY = 'radio_cart';

function getInitialCart() {
  if (typeof window === 'undefined') return { items: [], total: 0 };
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return { items: [], total: 0 };
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return { items: [], total: 0 };
    const items = parsed.items
      .filter((i) => i && (i.type === 'album' || i.type === 'track') && (i.item_id != null || i.item_uuid != null || i.uuid != null))
      .map((i) => ({
        type: i.type,
        item_id: i.item_id,
        item_uuid: i.item_uuid ?? i.uuid ?? null,
        uuid: i.uuid ?? i.item_uuid ?? null,
        quantity: typeof i.quantity === 'number' && i.quantity >= 1 ? i.quantity : 1
      }));
    return { items, total: typeof parsed.total === 'number' ? parsed.total : 0 };
  } catch {
    return { items: [], total: 0 };
  }
}

// Initial state
const initialState = {
  // Auth
  user: null,
  isAuthenticated: getInitialAuthState(), // Check token immediately
  isLoading: false,
  
  // Purchases (cached)
  owned: {
    trackUuids: {}, // { [trackUuid: string]: true }
    albumIds: {},   // { [albumId: string]: true }
    isLoading: false,
    loadedAt: null
  },
  
  // Mobile menu
  isMobileMenuOpen: false,
  isUserMenuOpen: false,
  
  // YouTube (link/playlist from backend)
  streamConfig: null,
  
  // Shop
  cart: getInitialCart(),
  
  // Social
  notifications: [],
  unreadCount: 0,
  
  // UI
  activeSection: 'home',
  darkMode: true,
  sidebarOpen: false,
  playerOpen: false,
  displayCurrency: 'PLN',
  
  // Audio Player - never restore from localStorage on refresh (player starts empty)
  currentTrack: null,
  playlist: [],
  currentTrackIndex: 0,
  isPlaying: false,
  isLoadingTrack: false,
  
  // Data
  featuredContent: null,
  
  // Messages
  error: null,
  success: null,
  warning: null,
  info: null,

  // Backend unreachable (e.g. server down, proxy ECONNREFUSED)
  backendUnavailable: false,

  // True only after initial auth check (refresh/session) has finished – avoids header/layout flicker on load
  authReady: false
};

// Action types
export const actionTypes = {
  // Auth
  SET_LOADING: 'SET_LOADING',
  SET_USER: 'SET_USER',
  SET_AUTH: 'SET_AUTH',
  UPDATE_USER: 'UPDATE_USER',
  LOGOUT: 'LOGOUT',
  
  // Mobile menu
  SET_MOBILE_MENU_OPEN: 'SET_MOBILE_MENU_OPEN',
  SET_USER_MENU_OPEN: 'SET_USER_MENU_OPEN',
  CLOSE_ALL_MENUS: 'CLOSE_ALL_MENUS',
  
  // YouTube config (link/playlist)
  SET_STREAM_CONFIG: 'SET_STREAM_CONFIG',
  
  // Cart
  ADD_TO_CART: 'ADD_TO_CART',
  REMOVE_FROM_CART: 'REMOVE_FROM_CART',
  UPDATE_CART_ITEM: 'UPDATE_CART_ITEM',
  UPDATE_CART: 'UPDATE_CART',
  CLEAR_CART: 'CLEAR_CART',
  UPDATE_CART_TOTAL: 'UPDATE_CART_TOTAL',
  
  // Social
  ADD_NOTIFICATION: 'ADD_NOTIFICATION',
  MARK_NOTIFICATION_READ: 'MARK_NOTIFICATION_READ',
  CLEAR_NOTIFICATIONS: 'CLEAR_NOTIFICATIONS',
  
  // UI
  SET_ACTIVE_SECTION: 'SET_ACTIVE_SECTION',
  SET_DISPLAY_CURRENCY: 'SET_DISPLAY_CURRENCY',
  TOGGLE_DARK_MODE: 'TOGGLE_DARK_MODE',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  TOGGLE_PLAYER: 'TOGGLE_PLAYER',
  
  // Audio Player
  PLAY_TRACK: 'PLAY_TRACK',
  PLAY_ALBUM: 'PLAY_ALBUM',
  PLAY_PLAYLIST: 'PLAY_PLAYLIST',
  PAUSE_PLAYER: 'PAUSE_PLAYER',
  RESUME_PLAYER: 'RESUME_PLAYER',
  STOP_PLAYER: 'STOP_PLAYER',
  PLAY_NEXT: 'PLAY_NEXT',
  PLAY_NEXT_TRACK: 'PLAY_NEXT_TRACK',
  PLAY_PREVIOUS: 'PLAY_PREVIOUS',
  SET_PLAYLIST: 'SET_PLAYLIST',
  SET_CURRENT_TRACK_INDEX: 'SET_CURRENT_TRACK_INDEX',
  SET_LOADING_TRACK: 'SET_LOADING_TRACK',
  
  // Data
  SET_FEATURED_CONTENT: 'SET_FEATURED_CONTENT',
  
  // Messages
  SET_ERROR: 'SET_ERROR',
  SET_SUCCESS: 'SET_SUCCESS',
  SET_WARNING: 'SET_WARNING',
  SET_INFO: 'SET_INFO',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  
  // Cart
  CALCULATE_CART_TOTAL: 'CALCULATE_CART_TOTAL',
  
  // Purchases (cached)
  SET_OWNED: 'SET_OWNED',
  SET_OWNED_LOADING: 'SET_OWNED_LOADING',

  SET_BACKEND_UNAVAILABLE: 'SET_BACKEND_UNAVAILABLE',
  SET_AUTH_READY: 'SET_AUTH_READY'
};

// Reducer
const appReducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
      
    case actionTypes.SET_USER: {
      const user = action.payload;
      const preferred = normalizeDisplayCurrency(user?.preferred_display_currency);
      const hasPreferred = Boolean(user?.preferred_display_currency);
      return {
        ...state,
        user,
        isAuthenticated: !!user,
        ...(hasPreferred ? { displayCurrency: preferred } : {}),
      };
    }
      
    case actionTypes.SET_AUTH:
      // Backwards-compatible: some call sites dispatch boolean here.
      if (typeof action.payload === 'boolean') {
        return {
          ...state,
          isAuthenticated: action.payload
        };
      }
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: action.payload.isAuthenticated
      };
      
    case actionTypes.UPDATE_USER:
      return {
        ...state,
        user: action.payload
      };
      
    case actionTypes.LOGOUT:
      // Player is cleared only on logout (and token expiry / idle timeout). Normal navigation must not reset playback.
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        cart: { items: [], total: 0 },
        notifications: [],
        // Clear player state on logout
        currentTrack: null,
        isPlaying: false,
        playlist: [],
        currentTrackIndex: 0,
        isLoadingTrack: false
      };
      
    case actionTypes.SET_MOBILE_MENU_OPEN:
      return { 
        ...state, 
        isMobileMenuOpen: action.payload,
        isUserMenuOpen: action.payload ? false : state.isUserMenuOpen // Close user menu when opening mobile menu
      };
      
    case actionTypes.SET_USER_MENU_OPEN:
      return { 
        ...state, 
        isUserMenuOpen: action.payload,
        isMobileMenuOpen: action.payload ? false : state.isMobileMenuOpen // Close mobile menu when opening user menu
      };
      
    case actionTypes.CLOSE_ALL_MENUS:
      return { 
        ...state, 
        isMobileMenuOpen: false,
        isUserMenuOpen: false
      };
      
    case actionTypes.SET_STREAM_CONFIG:
      return { ...state, streamConfig: action.payload };
      
    case actionTypes.ADD_TO_CART: {
      const newItem = action.payload;
      const existingIndex = state.cart.items.findIndex(
        item => item.type === newItem.type && item.item_id === newItem.item_id
      );
      
      let updatedItems;
      if (existingIndex >= 0) {
        // Digital products: never increment quantity; treat as already in cart.
        updatedItems = state.cart.items;
      } else {
        updatedItems = [...state.cart.items, newItem];
      }
      
      return {
        ...state,
        cart: { ...state.cart, items: updatedItems }
      };
    }
      
    case actionTypes.REMOVE_FROM_CART:
      return {
        ...state,
        cart: {
          ...state.cart,
          items: state.cart.items.filter((_, index) => index !== action.payload)
        }
      };
      
    case actionTypes.UPDATE_CART_ITEM:
      return {
        ...state,
        cart: {
          ...state.cart,
          items: state.cart.items.map((item, index) =>
            index === action.payload.index 
              ? { ...item, quantity: action.payload.quantity }
              : item
          )
        }
      };
      
    case actionTypes.UPDATE_CART:
      return {
        ...state,
        cart: { ...state.cart, items: action.payload }
      };
      
    case actionTypes.CLEAR_CART:
      return { ...state, cart: { items: [], total: 0 } };
      
    case actionTypes.UPDATE_CART_TOTAL:
      return { 
        ...state, 
        cart: { ...state.cart, total: action.payload } 
      };
      
    case actionTypes.ADD_NOTIFICATION:
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + 1
      };
      
    case actionTypes.MARK_NOTIFICATION_READ:
      return {
        ...state,
        notifications: state.notifications.map(notif =>
          notif.id === action.payload ? { ...notif, is_read: true } : notif
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      };
      
    case actionTypes.CLEAR_NOTIFICATIONS:
      return {
        ...state,
        notifications: [],
        unreadCount: 0
      };
      
    case actionTypes.SET_ACTIVE_SECTION:
      return { ...state, activeSection: action.payload };

    case actionTypes.SET_DISPLAY_CURRENCY:
      return { ...state, displayCurrency: action.payload || 'PLN' };

    case actionTypes.TOGGLE_DARK_MODE:
      return { ...state, darkMode: !state.darkMode };
      
    case actionTypes.TOGGLE_SIDEBAR:
      return { ...state, sidebarOpen: !state.sidebarOpen };
      
    case actionTypes.TOGGLE_PLAYER:
      return { ...state, playerOpen: !state.playerOpen };
      
    case actionTypes.SET_FEATURED_CONTENT:
      return { ...state, featuredContent: action.payload };
      
    case actionTypes.SET_ERROR:
      return { ...state, error: action.payload, success: null, warning: null, info: null };
      
    case actionTypes.SET_SUCCESS:
      return { ...state, success: action.payload, error: null, warning: null, info: null };
      
    case actionTypes.SET_WARNING:
      return { ...state, warning: action.payload, error: null, success: null, info: null };
      
    case actionTypes.SET_INFO:
      return { ...state, info: action.payload, error: null, success: null, warning: null };
      
    case actionTypes.CLEAR_MESSAGES:
      return { ...state, error: null, success: null, warning: null, info: null };
      
    case actionTypes.CALCULATE_CART_TOTAL:
      return {
        ...state,
        cart: { ...state.cart, total: state.cart.items.reduce((total, item) => total + item.price * item.quantity, 0) }
      };
      
    case actionTypes.SET_OWNED: {
      const trackUuids = action.payload?.trackUuids || {};
      const albumIds = action.payload?.albumIds || {};
      let currentTrack = state.currentTrack;
      const playingUuid = currentTrack?.uuid != null ? String(currentTrack.uuid) : '';
      if (playingUuid && trackUuids[playingUuid] && currentTrack.user_can_download !== true) {
        currentTrack = { ...currentTrack, user_can_download: true };
      }
      return {
        ...state,
        owned: {
          ...state.owned,
          trackUuids,
          albumIds,
          loadedAt: action.payload?.loadedAt || new Date().toISOString(),
          isLoading: false,
        },
        ...(currentTrack !== state.currentTrack ? { currentTrack } : {}),
      };
    }
      
    case actionTypes.SET_OWNED_LOADING:
      return {
        ...state,
        owned: {
          ...state.owned,
          isLoading: Boolean(action.payload)
        }
      };

    case actionTypes.SET_BACKEND_UNAVAILABLE:
      return { ...state, backendUnavailable: !!action.payload };

    case actionTypes.SET_AUTH_READY:
      return { ...state, authReady: !!action.payload };
      
    // Audio Player cases
    case actionTypes.PLAY_TRACK:
      if (process.env.NODE_ENV === 'development') {
        console.debug('[Player] PLAY_TRACK dispatched', action.payload?.uuid, action.payload?.title);
      }
      return {
        ...state,
        currentTrack: action.payload,
        isPlaying: true,
        playlist: [action.payload],
        currentTrackIndex: 0,
        isLoadingTrack: false
      };
      
    case actionTypes.PLAY_ALBUM: {
      // Ensure all tracks have album_id for proper state tracking
      const albumIdentifier = action.payload.id || action.payload.uuid;
      const tracksWithAlbumId = (action.payload.tracks || []).map(track => ({
        ...track,
        album_id: track.album_id || albumIdentifier,
        album: track.album || albumIdentifier,
        album_uuid: track.album_uuid || action.payload.uuid
      }));
      return {
        ...state,
        currentTrack: tracksWithAlbumId[0] || null,
        isPlaying: true,
        playlist: tracksWithAlbumId,
        currentTrackIndex: 0,
        isLoadingTrack: false
      };
    }
      
    case actionTypes.PLAY_PLAYLIST:
      return {
        ...state,
        currentTrack: action.payload.tracks?.[0] || null,
        isPlaying: true,
        playlist: action.payload.tracks || [],
        currentTrackIndex: 0
      };
      
    case actionTypes.PAUSE_PLAYER:
      return {
        ...state,
        isPlaying: false
      };
      
    case actionTypes.RESUME_PLAYER:
      return {
        ...state,
        isPlaying: true
      };
      
    case actionTypes.STOP_PLAYER:
      // Clears playback state. Used only on: logout, token expiry, idle timeout, or removeTracksFromPlayer (current track removed / playlist empty).
      return {
        ...state,
        currentTrack: null,
        isPlaying: false,
        playlist: [],
        currentTrackIndex: 0,
        isLoadingTrack: false
      };
      
    case actionTypes.PLAY_NEXT:
      if (state.playlist.length > 0 && state.currentTrackIndex < state.playlist.length - 1) {
        const nextIndex = state.currentTrackIndex + 1;
        return {
          ...state,
          currentTrack: state.playlist[nextIndex],
          currentTrackIndex: nextIndex,
          isPlaying: true
        };
      }
      // At end of playlist - pause (track finished, nothing to play next)
      return { ...state, isPlaying: false };

    case actionTypes.PLAY_NEXT_TRACK:
      return {
        ...state,
        currentTrack: action.payload.track,
        currentTrackIndex: action.payload.index,
        isPlaying: true,
        isLoadingTrack: false
      };
      
    case actionTypes.PLAY_PREVIOUS:
      if (state.playlist.length > 0 && state.currentTrackIndex > 0) {
        const prevIndex = state.currentTrackIndex - 1;
        return {
          ...state,
          currentTrack: state.playlist[prevIndex],
          currentTrackIndex: prevIndex,
          isPlaying: true
        };
      }
      return state;
      
    case actionTypes.SET_PLAYLIST:
      return {
        ...state,
        playlist: action.payload
      };
      
    case actionTypes.SET_CURRENT_TRACK_INDEX:
      return {
        ...state,
        currentTrackIndex: action.payload,
        currentTrack: state.playlist[action.payload] || null
      };
      
    case actionTypes.SET_LOADING_TRACK:
      return { ...state, isLoadingTrack: action.payload };
      
    default:
      return state;
  }
};

// Context - from appContextRef to avoid circular dependency with useApp.js
export { AppContext } from './appContextRef';

// Provider component
export const AppProvider = ({ children }) => {
  const [state, dispatch] = useReducer(appReducer, initialState, (s) => ({
    ...s,
    displayCurrency: normalizeDisplayCurrency(
      (typeof localStorage !== 'undefined' && localStorage.getItem('displayCurrency')) || 'PLN'
    )
  }));
  const abortControllerRef = useRef(null);
  const initializeAppRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    try {
      localStorage.setItem('displayCurrency', normalizeDisplayCurrency(state.displayCurrency));
    } catch (_) {}
  }, [state.displayCurrency]);

  // Persist cart to localStorage whenever it changes (survives refresh / new tab)
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      const { cart } = state;
      if (!cart || !Array.isArray(cart.items)) return;
      if (cart.items.length === 0) {
        localStorage.removeItem(CART_STORAGE_KEY);
        return;
      }
      localStorage.setItem(
        CART_STORAGE_KEY,
        JSON.stringify({
          items: cart.items.map(({ type, item_id, item_uuid, uuid, quantity }) => ({
            type,
            item_id,
            item_uuid: item_uuid ?? uuid ?? null,
            uuid: uuid ?? item_uuid ?? null,
            quantity: quantity ?? 1
          })),
          total: cart.total
        })
      );
    } catch (e) {
      console.warn('Cart persist failed', e);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cart]);

  // Actions - stable reference (use stateRef.current inside) so consumers' useEffects don't re-run on every state change (e.g. play/pause)
  const actions = useMemo(() => ({
    // Auth actions
    setLoading: (loading) => dispatch({ type: actionTypes.SET_LOADING, payload: loading }),
    
    setUser: (user) => dispatch({ type: actionTypes.SET_USER, payload: user }),

    /** Set auth state (for tests or session restore). Pass true to set authenticated, false to clear. */
    setAuth: (isAuthenticated) => dispatch({ type: actionTypes.SET_AUTH, payload: isAuthenticated }),

    updateUser: (user) => dispatch({ type: actionTypes.UPDATE_USER, payload: user }),
    
    refreshUser: async () => {
      try {
        const user = await apiService.getCurrentUser();
        dispatch({ type: actionTypes.SET_USER, payload: user });
        return user;
      } catch (error) {
        console.error('Failed to refresh user:', error);
        throw error;
      }
    },
    
    refreshOwnedPurchases: async () => {
      if (!stateRef.current.isAuthenticated) return null;
      try {
        dispatch({ type: actionTypes.SET_OWNED_LOADING, payload: true });
        const purchases = await queryClient.fetchQuery({
          queryKey: queryKeys.shop.userPurchases,
          queryFn: fetchUserPurchases,
        });
        const { trackUuids, albumIds } = ownedMapsFromPurchases(purchases);
        dispatch({
          type: actionTypes.SET_OWNED,
          payload: { trackUuids, albumIds, loadedAt: new Date().toISOString() },
        });
        return { trackUuids, albumIds };
      } catch (error) {
        dispatch({ type: actionTypes.SET_OWNED_LOADING, payload: false });
        return null;
      }
    },
    
    login: async (email, password, code = null, rememberMe = false) => {
      try {
        dispatch({ type: actionTypes.SET_LOADING, payload: true });
        const response = await apiService.login(email, password, code, rememberMe);
        
        // Check if 2FA is required
        if (response.requires_2fa) {
          return { requires_2fa: true, user_id: response.user_id, message: response.message };
        }
        
        if (response.access_token) {
          apiService.setAuthToken(response.access_token);
          setSessionHint(rememberMe);

          // Use user data from login response to avoid extra API call
          // Login response already contains fresh user data
          if (response.user) {
            dispatch({ type: actionTypes.SET_USER, payload: response.user });
          } else {
            // Fallback: get user data only if not in response
            try {
              const userData = await apiService.getCurrentUser();
              dispatch({ type: actionTypes.SET_USER, payload: userData });
            } catch (profileError) {
              console.error('Failed to get user profile:', profileError);
            }
          }
          
          dispatch({ type: actionTypes.SET_SUCCESS, payload: i18n.t('auth:success.login_successful') });
          // Owned purchases load via UserPurchasesQuerySync (React Query dedupe)
          return true;
        }
        return false;
      } catch (error) {
        // Handle detailed error messages from backend
        if (error.data) {
          const d = error.data;
          const needsVerify = d.requires_verification || d.details?.requires_verification;
          // Check if email verification is required
          if (needsVerify) {
            const errorMessage =
              d.error ||
              (typeof d.message === 'string' ? d.message : '') ||
              i18n.t('auth:errors.verify_email_before_login');
            dispatch({ type: actionTypes.SET_ERROR, payload: errorMessage });
            return {
              error: errorMessage,
              requires_verification: true,
              email: d.email || d.details?.email
            };
          }

          if (d.error === 'maintenance_mode') {
            const notice = i18n.t('common:maintenance.auth_notice');
            dispatch({ type: actionTypes.SET_ERROR, payload: notice });
            return { error: notice, maintenance: true };
          }

          const rawMessage = getAuthApiDisplayMessage(error, i18n.t.bind(i18n));
          const errorMessage = rawMessage || i18n.t('auth:errors.login_failed');
          const toShow = mapAuthErrorMessage(errorMessage);
          dispatch({ type: actionTypes.SET_ERROR, payload: toShow });
          return { error: toShow };
        }
        
        const loginFailedMsg = i18n.t('auth:errors.login_failed');
        dispatch({ type: actionTypes.SET_ERROR, payload: loginFailedMsg });
        return { error: loginFailedMsg };
      } finally {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    },
    
    register: async (userData, options = {}) => {
      try {
        dispatch({ type: actionTypes.SET_LOADING, payload: true });
        const _response = await apiService.register(userData);
        // Callers own success UX (AuthPage success screen, onboarding checkEmail).
        // Opt-in toast only — default message must not say "go login" before email verify.
        if (options.notifySuccess) {
          dispatch({
            type: actionTypes.SET_SUCCESS,
            payload:
              options.successMessage ||
              i18n.t('auth:success.account_created_check_email'),
          });
        }
        return { success: true };
      } catch (error) {
        // Handle registration disabled error
        const regDisabled =
          error.data &&
          (error.data.registration_disabled || error.data.details?.registration_disabled);
        if (regDisabled) {
          const errorMessage = i18n.t('auth:errors.registration_disabled');
          dispatch({ type: actionTypes.SET_ERROR, payload: errorMessage });
          return { success: false, error: errorMessage };
        }

        // Handle detailed error messages from backend
        if (error.data) {
          const errorMessage =
            getAuthApiDisplayMessage(error, i18n.t.bind(i18n)) ||
            i18n.t('auth:errors.registration_failed');
          dispatch({ type: actionTypes.SET_ERROR, payload: errorMessage });
          return { success: false, error: errorMessage };
        }
        
        const regFailedMsg = i18n.t('auth:errors.registration_failed');
        dispatch({ type: actionTypes.SET_ERROR, payload: regFailedMsg });
        return { success: false, error: regFailedMsg };
      } finally {
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      }
    },

    logout: async () => {
      const accessToken = apiService.getToken();
      clearSessionHint();
      localStorage.removeItem('playerState');
      apiService.setAuthToken(null);
      dispatch({ type: actionTypes.STOP_PLAYER });
      dispatch({ type: actionTypes.LOGOUT });
      dispatch({
        type: actionTypes.SET_SUCCESS,
        payload: i18n.t('auth:success.logout_successful'),
      });
      try {
        await apiService.logout(accessToken);
      } catch (error) {
        console.error('Logout error:', error);
      }
    },
    
    // Cart actions
    addToCart: (item) => {
      const cartItems = stateRef.current.cart.items || [];
      const safeTitle = (value, fallback) => {
        const s = typeof value === 'string' ? value.trim() : '';
        return s ? s : fallback;
      };
      const itemTitle = safeTitle(item?.title, item?.type === 'album' ? 'Album' : 'Track');
      
      const sameItemExists = cartItems.some(
        (ci) => ci.type === item.type && String(ci.item_id) === String(item.item_id)
      );
      if (sameItemExists) {
        dispatch({ type: actionTypes.SET_INFO, payload: i18n.t('shop:messages.item_already_in_cart', { itemTitle }) });
        return;
      }
      
      const matchesAlbum = (albumCartItem, albumIdOrUuid) => {
        if (!albumIdOrUuid) return false;
        const key = String(albumIdOrUuid);
        return String(albumCartItem?.item_id) === key || String(albumCartItem?.item_uuid) === key;
      };
      
      if (item?.type === 'track') {
        // If the album is already in cart, block adding the track.
        const albumId = item.album_id || (item.album && item.album.id);
        const albumUuid = item.album_uuid || (item.album && item.album.uuid) || (typeof item.album === 'string' ? item.album : null);
        const albumInCart = cartItems.find((ci) => ci.type === 'album' && (matchesAlbum(ci, albumId) || matchesAlbum(ci, albumUuid)));
        if (albumInCart) {
          const albumTitle = safeTitle(item.album_title, safeTitle(albumInCart?.title, 'this album'));
          dispatch({
            type: actionTypes.SET_INFO,
            payload: i18n.t('shop:messages.item_part_of_album_in_cart', { itemTitle, albumTitle })
          });
          return;
        }
      }
      
      if (item?.type === 'album') {
        // Replace: remove tracks from this album, then add album.
        const albumId = item.item_id;
        const albumUuid = item.item_uuid;
        const isTrackFromAlbum = (ci) => {
          if (ci.type !== 'track') return false;
          const trackAlbumId = ci.album_id || (ci.album && ci.album.id);
          const trackAlbumUuid = ci.album_uuid || (ci.album && ci.album.uuid) || (typeof ci.album === 'string' ? ci.album : null);
          return (albumId && String(trackAlbumId) === String(albumId)) ||
            (albumUuid && String(trackAlbumUuid) === String(albumUuid));
        };
        
        const removedTracks = cartItems.filter(isTrackFromAlbum);
        if (removedTracks.length > 0) {
          const kept = cartItems.filter((ci) => !isTrackFromAlbum(ci));
          const alreadyHasAlbum = kept.some((ci) => ci.type === 'album' && String(ci.item_id) === String(albumId));
          const nextItems = alreadyHasAlbum ? kept : [...kept, item];
          dispatch({ type: actionTypes.UPDATE_CART, payload: nextItems });
          const albumTitle = itemTitle;
          dispatch({
            type: actionTypes.SET_SUCCESS,
            payload: i18n.t('shop:messages.album_added_tracks_removed', { albumTitle, count: removedTracks.length })
          });
          return;
        }
      }
      
      dispatch({ type: actionTypes.ADD_TO_CART, payload: item });
      dispatch({ type: actionTypes.SET_SUCCESS, payload: i18n.t('shop:messages.item_added_to_cart', { itemTitle }) });
    },
    
    removeFromCart: (index) => {
      dispatch({ type: actionTypes.REMOVE_FROM_CART, payload: index });
    },
    
    updateCartItem: (index, quantity) => {
      dispatch({ type: actionTypes.UPDATE_CART_ITEM, payload: { index, quantity } });
    },
    
    updateCart: (items) => {
      dispatch({ type: actionTypes.UPDATE_CART, payload: items });
    },
    
    clearCart: () => dispatch({ type: actionTypes.CLEAR_CART }),
    
    calculateCartTotal: async () => {
      dispatch({ type: actionTypes.CALCULATE_CART_TOTAL });
    },
    
    // UI actions
    setActiveSection: (section) => dispatch({ type: actionTypes.SET_ACTIVE_SECTION, payload: section }),
    setDisplayCurrency: (currency) => {
      const value = currency || 'PLN';
      try {
        localStorage.setItem('displayCurrency', value);
      } catch (_) {}
      dispatch({ type: actionTypes.SET_DISPLAY_CURRENCY, payload: value });
    },
    toggleDarkMode: () => dispatch({ type: actionTypes.TOGGLE_DARK_MODE }),
    toggleSidebar: () => dispatch({ type: actionTypes.TOGGLE_SIDEBAR }),
    togglePlayer: () => dispatch({ type: actionTypes.TOGGLE_PLAYER }),
    
    // Data actions
    loadFeaturedContent: async () => {
      try {
        const content = await queryClient.fetchQuery({
          queryKey: queryKeys.core.featuredContent,
          queryFn: () => apiService.getFeaturedContent(),
        });
        dispatch({ type: actionTypes.SET_FEATURED_CONTENT, payload: content });
      } catch (error) {
        if (error.status !== 429) console.error('Failed to load featured content:', error);
      }
    },

    loadStreamConfig: async () => {
      try {
        const config = await queryClient.fetchQuery({
          queryKey: queryKeys.core.streamConfig,
          queryFn: () => apiService.getStreamConfig(),
        });
        dispatch({ type: actionTypes.SET_STREAM_CONFIG, payload: config });
      } catch (error) {
        if (error.status !== 429) console.error('Failed to load stream config:', error);
      }
    },
    
    // Message actions
    setError: (message) => {
      dispatch({ type: actionTypes.SET_ERROR, payload: message });
    },
    setSuccess: (message) => {
      dispatch({ type: actionTypes.SET_SUCCESS, payload: message });
    },
    setWarning: (message) => dispatch({ type: actionTypes.SET_WARNING, payload: message }),
    setInfo: (message) => dispatch({ type: actionTypes.SET_INFO, payload: message }),
    clearMessages: () => {
      dispatch({ type: actionTypes.CLEAR_MESSAGES });
    },

    retryConnection: () => {
      initRefreshAttempted = false;
      appInitStartedFromEffect = false;
      dispatch({ type: actionTypes.SET_BACKEND_UNAVAILABLE, payload: false });
      initializeAppRef.current?.();
    },
    
    // Prompt login: toast only (no modal state)
    promptLogin: () => {
      dispatch({ type: actionTypes.SET_WARNING, payload: i18n.t('common:messages.login_to_play') });
    },
    loginSuccess: (user) => {
      dispatch({ type: actionTypes.SET_USER, payload: user });
      dispatch({ type: actionTypes.SET_SUCCESS, payload: i18n.t('auth:success.login_successful') });
    },
    
    // Mobile menu actions
    setMobileMenuOpen: (isOpen) => dispatch({ type: actionTypes.SET_MOBILE_MENU_OPEN, payload: isOpen }),
    setUserMenuOpen: (isOpen) => dispatch({ type: actionTypes.SET_USER_MENU_OPEN, payload: isOpen }),
    closeAllMenus: () => dispatch({ type: actionTypes.CLOSE_ALL_MENUS }),
    
    // Notification actions
    addNotification: (notification) => {
      const id = Date.now();
      dispatch({ 
        type: actionTypes.ADD_NOTIFICATION, 
        payload: { ...notification, id, is_read: false, created_at: new Date() }
      });
    },
    
    // Audio Player actions
    playTrack: async (track) => {
      // SECURITY: Block anonymous users
      if (!stateRef.current.isAuthenticated) {
        dispatch({ type: actionTypes.SET_WARNING, payload: i18n.t('common:messages.login_to_play') });
        return;
      }

      // REMOVED: "same track" check - components handle toggle when same track.
      // playTrack is only called for different tracks.

      if (process.env.NODE_ENV === 'development') {
        console.debug('[Player] playTrack called', track?.uuid || track?.id, track?.title);
      }

      stopAllUploadPreviewPlayback();

      // Abort any in-flight request before starting new one (prevents race conditions)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const ac = new AbortController();
      abortControllerRef.current = ac;
      dispatch({ type: actionTypes.SET_LOADING_TRACK, payload: true });
      
      // Different track - fetch fresh data and play
      // If track has uuid, fetch fresh data from API to ensure preview_url is up-to-date
      // This is important because preview generation happens asynchronously after upload
      if (track && track.uuid) {
        try {
          const endpoint = `/api/tracks/${track.uuid}/`;
          const freshTrack = await apiService.get(endpoint, { signal: ac.signal });
          if (ac.signal.aborted) {
            if (abortControllerRef.current === ac) abortControllerRef.current = null;
            dispatch({ type: actionTypes.SET_LOADING_TRACK, payload: false });
            return;
          }
          const ownedUuids = stateRef.current.owned?.trackUuids || {};
          const uuidKey = String(track.uuid || '');
          const canDownload =
            freshTrack?.user_can_download === true ||
            track?.user_can_download === true ||
            Boolean(uuidKey && ownedUuids[uuidKey]);
          const trackToDispatch = {
            ...(freshTrack || track),
            user_can_download: canDownload,
          };
          dispatch({ type: actionTypes.PLAY_TRACK, payload: trackToDispatch });
        } catch (error) {
          if (ac.signal.aborted) {
            if (abortControllerRef.current === ac) abortControllerRef.current = null;
            dispatch({ type: actionTypes.SET_LOADING_TRACK, payload: false });
            return;
          }
          dispatch({ type: actionTypes.PLAY_TRACK, payload: track });
        } finally {
          if (abortControllerRef.current === ac) abortControllerRef.current = null;
          dispatch({ type: actionTypes.SET_LOADING_TRACK, payload: false });
        }
      } else {
        dispatch({ type: actionTypes.SET_LOADING_TRACK, payload: false });
        dispatch({ type: actionTypes.PLAY_TRACK, payload: track });
      }
    },
    
    playAlbum: async (album) => {
      // SECURITY: Block anonymous users
      if (!stateRef.current.isAuthenticated) {
        dispatch({ type: actionTypes.SET_WARNING, payload: i18n.t('common:messages.login_to_play') });
        return;
      }

      stopAllUploadPreviewPlayback();

      // Abort any in-flight request before starting new one (prevents race conditions)
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const ac = new AbortController();
      abortControllerRef.current = ac;
      dispatch({ type: actionTypes.SET_LOADING_TRACK, payload: true });

      let albumToPlay = album;
      let tracks = album?.tracks || [];

      try {
        // If album has no tracks (e.g. from list endpoint), fetch full album with tracks
        if (tracks.length === 0 && (album?.uuid || album?.id)) {
          try {
            const fullAlbum = await apiService.get(`/api/albums/${album.uuid || album.id}/`, { signal: ac.signal });
            if (ac.signal.aborted) return;
            if (fullAlbum?.tracks?.length) {
              albumToPlay = fullAlbum;
              tracks = fullAlbum.tracks;
            }
          } catch (e) {
            if (ac.signal.aborted) return;
          }
        }

        if (tracks.length === 0) {
          dispatch({ type: actionTypes.PLAY_ALBUM, payload: albumToPlay });
          return;
        }

        // Fetch full data for the first track so it has preview_url (like playTrack)
        const first = tracks[0];
        let firstTrack = first;
        if (first?.uuid) {
          try {
            const fresh = await apiService.get(`/api/tracks/${first.uuid}/`, { signal: ac.signal });
            if (ac.signal.aborted) return;
            if (fresh) firstTrack = fresh;
          } catch (e) {
            if (ac.signal.aborted) return;
          }
        }
        const tracksWithFreshFirst = [firstTrack, ...tracks.slice(1)];
        dispatch({ type: actionTypes.PLAY_ALBUM, payload: { ...albumToPlay, tracks: tracksWithFreshFirst } });
      } finally {
        if (abortControllerRef.current === ac) abortControllerRef.current = null;
        dispatch({ type: actionTypes.SET_LOADING_TRACK, payload: false });
      }
    },
    
    togglePlayPause: () => {
      // SECURITY: Block anonymous users
      if (!stateRef.current.isAuthenticated) {
        dispatch({ type: actionTypes.SET_WARNING, payload: i18n.t('common:messages.login_to_play') });
        return;
      }
      
      if (stateRef.current.isPlaying) {
        dispatch({ type: actionTypes.PAUSE_PLAYER });
      } else {
        dispatch({ type: actionTypes.RESUME_PLAYER });
      }
    },
    
    playPlaylist: (playlist) => {
      // SECURITY: Block anonymous users
      if (!stateRef.current.isAuthenticated) {
        dispatch({ type: actionTypes.SET_WARNING, payload: i18n.t('common:messages.login_to_play') });
        return;
      }
      
      dispatch({ type: actionTypes.PLAY_PLAYLIST, payload: playlist });
    },
    
    pausePlayer: () => {
      dispatch({ type: actionTypes.PAUSE_PLAYER });
    },
    
    resumePlayer: () => {
      dispatch({ type: actionTypes.RESUME_PLAYER });
    },
    
    stopPlayer: () => {
      dispatch({ type: actionTypes.STOP_PLAYER });
    },
    
    playNext: async () => {
      const s = stateRef.current;
      if (!s.isAuthenticated) {
        return;
      }
      if (s.playlist.length === 0 || s.currentTrackIndex >= s.playlist.length - 1) {
        dispatch({ type: actionTypes.PAUSE_PLAYER });
        return;
      }

      const nextIndex = s.currentTrackIndex + 1;
      const nextTrack = s.playlist[nextIndex];
      if (!nextTrack) {
        dispatch({ type: actionTypes.PAUSE_PLAYER });
        return;
      }

      stopAllUploadPreviewPlayback();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const ac = new AbortController();
      abortControllerRef.current = ac;
      dispatch({ type: actionTypes.SET_LOADING_TRACK, payload: true });

      try {
        let trackToPlay = nextTrack;
        if (nextTrack.uuid) {
          try {
            const freshTrack = await apiService.get(`/api/tracks/${nextTrack.uuid}/`, {
              signal: ac.signal,
            });
            if (!ac.signal.aborted) {
              const ownedUuids = s.owned?.trackUuids || {};
              const uuidKey = String(nextTrack.uuid || '');
              const canDownload =
                freshTrack?.user_can_download === true ||
                nextTrack?.user_can_download === true ||
                Boolean(uuidKey && ownedUuids[uuidKey]);
              trackToPlay = {
                ...(freshTrack || nextTrack),
                user_can_download: canDownload,
              };
            }
          } catch {
            if (!ac.signal.aborted) {
              trackToPlay = nextTrack;
            }
          }
        }
        if (ac.signal.aborted) {
          return;
        }
        dispatch({
          type: actionTypes.PLAY_NEXT_TRACK,
          payload: { track: trackToPlay, index: nextIndex },
        });
      } finally {
        if (abortControllerRef.current === ac) {
          abortControllerRef.current = null;
        }
        dispatch({ type: actionTypes.SET_LOADING_TRACK, payload: false });
      }
    },
    
    playPrevious: () => {
      dispatch({ type: actionTypes.PLAY_PREVIOUS });
    },
    
    setPlaylist: (playlist) => {
      dispatch({ type: actionTypes.SET_PLAYLIST, payload: playlist });
    },
    
    setCurrentTrackIndex: (index) => {
      dispatch({ type: actionTypes.SET_CURRENT_TRACK_INDEX, payload: index });
    },

    removeTracksFromPlayer: (uuids) => {
      const set = new Set(Array.isArray(uuids) ? uuids : [uuids]);
      if (set.has(stateRef.current.currentTrack?.uuid)) {
        dispatch({ type: actionTypes.STOP_PLAYER });
        return;
      }
      const newPlaylist = stateRef.current.playlist.filter((t) => !set.has(t.uuid));
      if (newPlaylist.length === 0) {
        dispatch({ type: actionTypes.STOP_PLAYER });
        return;
      }
      const idx = stateRef.current.currentTrack ? newPlaylist.findIndex((t) => t.uuid === stateRef.current.currentTrack.uuid) : 0;
      const newIndex = idx >= 0 ? idx : 0;
      dispatch({ type: actionTypes.SET_PLAYLIST, payload: newPlaylist });
      dispatch({ type: actionTypes.SET_CURRENT_TRACK_INDEX, payload: newIndex });
    }
  }), []);

  // Clear saved player state on load so refresh always starts with no track
  useEffect(() => {
    try {
      localStorage.removeItem('playerState');
    } catch (_) {}
  }, []);

  // Initialize app
  useEffect(() => {
    if (typeof window !== 'undefined' && window.__radioAppInitStarted) {
      return;
    }
    if (typeof window !== 'undefined') window.__radioAppInitStarted = true;
    if (appInitStartedFromEffect) {
      return;
    }
    appInitStartedFromEffect = true;

    const initializeApp = async () => {
      if (appInitInProgress) return;
      appInitInProgress = true;
      dispatch({ type: actionTypes.SET_LOADING, payload: false });
      try {
        try {
          const hasValidToken = apiService.getToken() && !apiService.isTokenExpired();
          if (hasValidToken) {
            const user = await apiService.getCurrentUser();
            dispatch({ type: actionTypes.SET_USER, payload: user });
            dispatch({ type: actionTypes.SET_AUTH, payload: { user, isAuthenticated: true } });
          } else if (!hasSessionHint()) {
            dispatch({ type: actionTypes.SET_AUTH, payload: { user: null, isAuthenticated: false } });
          } else if (initRefreshAttempted) {
            dispatch({ type: actionTypes.SET_AUTH, payload: { user: null, isAuthenticated: false } });
          } else {
            initRefreshAttempted = true;
            const refreshed = await apiService.refreshToken();
            const user = await apiService.getCurrentUser();
            dispatch({ type: actionTypes.SET_USER, payload: user });
            dispatch({ type: actionTypes.SET_AUTH, payload: { user, isAuthenticated: true } });
            setSessionHint(Boolean(refreshed?.rememberMe));
          }
        } catch (error) {
          // Backend unreachable: show single banner, stop loading, don't treat as logout
          if (isNetworkError(error)) {
            dispatch({ type: actionTypes.SET_BACKEND_UNAVAILABLE, payload: true });
            dispatch({ type: actionTypes.SET_LOADING, payload: false });
            return;
          }
          // Only clear session hint on real auth failure (401).
          const isSessionExpired = error?.status === 401;
          if (isSessionExpired) {
            clearSessionHint();
          } else {
            initRefreshAttempted = false;
          }
          dispatch({ type: actionTypes.SET_AUTH, payload: { user: null, isAuthenticated: false } });
        }
        // Core catalog: TanStack Query via <CoreCatalogQuerySync /> (prefetch on mount).
        queryClient.prefetchQuery({
          queryKey: queryKeys.core.streamConfig,
          queryFn: () => apiService.getStreamConfig(),
        });
        queryClient.prefetchQuery({
          queryKey: queryKeys.core.featuredContent,
          queryFn: () => apiService.getFeaturedContent(),
        });
        dispatch({ type: actionTypes.SET_LOADING, payload: false });
      } finally {
        appInitInProgress = false;
        dispatch({ type: actionTypes.SET_AUTH_READY, payload: true });
      }
    };
    initializeAppRef.current = initializeApp;
    initializeApp();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const wasAuthenticatedRef = useRef(false);
  useEffect(() => {
    wasAuthenticatedRef.current = state.isAuthenticated;
  }, [state.isAuthenticated]);

  // Handle token expiration – only show "session expired" if we actually had a session (avoids toast on init when no cookie)
  useEffect(() => {
    const handleTokenExpired = () => {
      const hadSession = wasAuthenticatedRef.current;
      clearSessionHint();
      dispatch({ type: actionTypes.STOP_PLAYER });
      dispatch({ type: actionTypes.LOGOUT });
      if (hadSession) {
        dispatch({ type: actionTypes.SET_ERROR, payload: i18n.t('common:messages.session_expired') });
      }
    };

    window.addEventListener('tokenExpired', handleTokenExpired);
    return () => window.removeEventListener('tokenExpired', handleTokenExpired);
  }, []);

  // Session timeout (idle): log out after N minutes of inactivity when user has session_timeout > 0
  const idleTimeoutRef = useRef(null);
  const idleDebounceRef = useRef(null);
  useEffect(() => {
    const sessionTimeoutMinutes = state.user?.session_timeout ?? 0;
    if (!state.isAuthenticated || !state.user || sessionTimeoutMinutes <= 0) {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      if (idleDebounceRef.current) clearTimeout(idleDebounceRef.current);
      idleTimeoutRef.current = idleDebounceRef.current = null;
      return;
    }
    const timeoutMs = sessionTimeoutMinutes * 60 * 1000;
    const DEBOUNCE_MS = 1000; // reset timer 1s after last activity to avoid storm on mousemove

    const scheduleLogout = () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      idleTimeoutRef.current = setTimeout(() => {
        idleTimeoutRef.current = null;
        apiService.setAuthToken(null);
        clearSessionHint();
        dispatch({ type: actionTypes.STOP_PLAYER });
        dispatch({ type: actionTypes.LOGOUT });
        dispatch({ type: actionTypes.SET_ERROR, payload: i18n.t('common:messages.session_expired_inactivity') });
      }, timeoutMs);
    };

    const onActivity = () => {
      if (idleDebounceRef.current) clearTimeout(idleDebounceRef.current);
      idleDebounceRef.current = setTimeout(() => {
        idleDebounceRef.current = null;
        scheduleLogout();
      }, DEBOUNCE_MS);
    };

    scheduleLogout();
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('click', onActivity);
    window.addEventListener('scroll', onActivity);
    window.addEventListener('touchstart', onActivity);
    // eslint-disable-next-line consistent-return
    return () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      if (idleDebounceRef.current) clearTimeout(idleDebounceRef.current);
      idleTimeoutRef.current = idleDebounceRef.current = null;
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('click', onActivity);
      window.removeEventListener('scroll', onActivity);
      window.removeEventListener('touchstart', onActivity);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthenticated, state.user?.session_timeout]);

  // Auto-clear messages
  /* eslint-disable consistent-return */
  useEffect(() => {
    if (state.error || state.success || state.warning || state.info) {
      const timer = setTimeout(() => {
        dispatch({ type: actionTypes.CLEAR_MESSAGES });
      }, 5000);
      return () => clearTimeout(timer);
    }
    return () => {};
  }, [state.error, state.success, state.warning, state.info]);
  /* eslint-enable consistent-return */

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ state, actions }), [state, actions]);

  return (
    <AppContext.Provider value={contextValue}>
      <CoreCatalogQuerySync dispatch={dispatch} />
      <UserPurchasesQuerySync dispatch={dispatch} isAuthenticated={state.isAuthenticated} />
      {children}
    </AppContext.Provider>
  );
};
